/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The renderer registry.
 *
 * ── Adding support for a new entity type ────────────────────────────────────
 *   1. Write `MyEntityRenderer.tsx` exporting a `React.FC<RendererProps>`.
 *   2. Add one line to REGISTRY below.
 * That is the whole change. The dispatcher never needs editing, and an entity type that is *not*
 * registered — including one the backend adds after this build ships — falls through to
 * GenericDiffRenderer rather than rendering blank.
 * ────────────────────────────────────────────────────────────────────────────
 */

import React from 'react'

import { AlertConfigRenderer } from './AlertConfigRenderer'
import { AlertSubscriptionRenderer } from './AlertSubscriptionRenderer'
import { AssetRenderer } from './AssetRenderer'
import { CreateRenderer, DeleteRenderer } from './EntitySnapshotRenderer'
import { GenericDiffRenderer } from './GenericDiffRenderer'
import { GeofenceRenderer } from './GeofenceRenderer'
import type { EntityRenderer, RendererProps } from './types'
import { UserAdminRenderer } from './UserAdminRenderer'
import { VirtualSetPointRenderer } from './VirtualSetPointRenderer'

import type { AuditDetail, EntityType } from '../../types'
import { normalizeDiff } from '../diff/normalize'
import { NoChangesPanel } from '../primitives'

const REGISTRY: Partial<Record<EntityType, EntityRenderer>> = {
  // A configuration's create embeds the whole document as one entry, which the shared snapshot card
  // renders as an unreadable blob; the renderer presents it as the settings it was created with.
  alertConfiguration: { Component: AlertConfigRenderer, handlesCreateDelete: true },
  // A subscription reads as a subscriber, a raise/clear × email/sms matrix and its asset groups.
  // It owns create and delete because those embed the whole entity as one snapshot, which the
  // structured card cannot present meaningfully.
  alertSubscription: { Component: AlertSubscriptionRenderer, handlesCreateDelete: true },
  // A setpoint's create embeds every zone's value in one empty-path entry, which the shared snapshot
  // card shows as "Record: 1 field"; the renderer unpacks it into a row per zone.
  virtualSetPoint: { Component: VirtualSetPointRenderer, handlesCreateDelete: true },
  geofence: { Component: GeofenceRenderer, handlesCreateDelete: true },
  asset: { Component: AssetRenderer },
  // A user's create and delete embed the whole record in one entry, and its privileges are an
  // index-diffed array that only reads correctly when reassembled into before and after sets.
  userAdministration: { Component: UserAdminRenderer, handlesCreateDelete: true },
}

const FALLBACK: EntityRenderer = { Component: GenericDiffRenderer }

/** The renderer registered for an entity type, or the generic fallback. */
export const rendererFor = (entityType: string): EntityRenderer => REGISTRY[entityType as EntityType] ?? FALLBACK

/**
 * Choose and run the renderer for a record.
 *
 * Three decisions, in order:
 *  1. A record with no usable changes — a malformed, empty or unrecognised diff — renders the
 *     shared no-changes panel. Handled here rather than in each renderer so that every entity
 *     type, including ones added later, is covered without having to remember.
 *  2. A create or delete uses the shared snapshot card, because neither has a meaningful other
 *     side. A registered renderer can opt out with `handlesCreateDelete` when it has something
 *     better to show.
 *  3. Otherwise the registry decides, falling back to the generic renderer.
 */
export const ChangeRenderer: React.FC<{ audit: AuditDetail }> = ({ audit }) => {
  const changes = React.useMemo(() => normalizeDiff(audit.diff), [audit.diff])
  const renderer = rendererFor(audit.entityType)

  const props: RendererProps = { audit, changes }

  if (changes.length === 0) return <NoChangesPanel />

  if (!renderer.handlesCreateDelete) {
    if (audit.changeType === 'create') return <CreateRenderer {...props} />
    if (audit.changeType === 'delete') return <DeleteRenderer {...props} />
  }

  const { Component } = renderer
  return <Component {...props} />
}

export type { EntityRenderer, RendererProps } from './types'
